import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {DiffEntry, DiffUser, PullRequestState} from '@github-ui/diff-lines/types'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import type {CommentingImplementation, MarkerNavigationImplementation} from '@github-ui/conversations'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import {
  isSelectingDiffLineOrRange,
  parsePathDigestWithoutLineNumbers,
} from '@github-ui/diff-lines/document-hash-helpers'
import {useSelectedFullDiffHash} from '@github-ui/diff-lines/use-selected-diff-hash'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import type {Repository} from '@github-ui/current-repository'
import {PullRequestDiff} from './PullRequestDiff'
// eslint-disable-next-line no-restricted-imports
import {getCurrentSize, ScreenSize} from '@github-ui/screen-size'
import {useResizeObserver} from '@primer/react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {testIdProps} from '@github-ui/test-id-props'
import {ProgressivePullRequestDiffEntry} from './progressive-diff/ProgressivePullRequestDiffEntry'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestDiffEntryErrorFallback} from './PullRequestDiffEntryErrorFallback'
import {useProgressiveDiffStore} from '../stores/ProgressiveDiffStore'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {useDiffSummaries} from '../page-data/loaders/use-diff-summaries-data'
import type {RepoSubject} from '@github-ui/comment-box/subject'

export const SHOW_WHIMSY_THRESHOLD = 15

export interface PullRequestDiffsListProps {
  basePath: string
  commentBoxConfig: CommentingImplementation['commentBoxConfig']
  commentBoxSubject?: RepoSubject
  contextLinesURL: string
  currentUser: DiffUser
  diffs: DiffEntry[]
  filteredDiffSummaries: PullRequestFileTreeDiff[]
  headBranchName: string
  pullRequestState: PullRequestState
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  commentBatchPending: boolean
  endOfDiffsImagePath?: string | undefined
}

const headerHeight = 41
const headerWrapperPadding = 16
const baseDiffHeight = headerHeight + headerWrapperPadding
const stickyHeaderHeight = 60 // height to be used when scrolling, the 4px difference looks nicer
export const PullRequestDiffsList = memo(PullRequestDiffsListUnmemoized)

function PullRequestDiffsListUnmemoized({
  basePath,
  commentBoxConfig,
  commentBoxSubject,
  contextLinesURL,
  currentUser,
  diffs,
  filteredDiffSummaries,
  headBranchName,
  pullRequestState,
  repository,
  commentBatchPending,
  endOfDiffsImagePath,
}: PullRequestDiffsListProps) {
  const [diffManuallyExpanded, __] = useState(false)
  const selectedFullDiffHash = useSelectedFullDiffHash()
  const selectedPathDigest = parsePathDigestWithoutLineNumbers(selectedFullDiffHash) ?? ''
  const isSelectingLineOrRange = isSelectingDiffLineOrRange(selectedFullDiffHash)

  const diffsParentRef = useRef<HTMLDivElement>(null)
  const markerNavigationImplementation: MarkerNavigationImplementation = {
    incrementActiveMarker: () => {},
    decrementActiveMarker: () => {},
    filteredMarkers: [],
    onActivateGlobalMarkerNavigation: () => {},
    activeGlobalMarkerID: undefined,
  }

  const shouldUseProgressiveDiff = useFeatureFlag('prx_files_progressive_diff')
  const testId = shouldUseProgressiveDiff ? 'progressive-diff' : 'regular-diff'

  const diffEntries = useProgressiveDiffStore(s => s.entries)

  /*
   * Scroll logic for progressive diffs.
   */
  const targetableEntriesRef = useRef<Map<string, HTMLElement>>(new Map())
  const getTargetableEntriesMap = useCallback(() => {
    return targetableEntriesRef.current
  }, [])

  const initialRenderRef = useRef(true)
  const onScrollToAndFocusEntry = (pathDigest: string) => {
    const map = getTargetableEntriesMap()
    const node = map.get(pathDigest)
    if (!node) return

    // By default, scroll to the entry and focus its toggle button
    let elementToScrollTo: HTMLElement | null = node
    let elementToFocus: HTMLElement | null = node.querySelector<HTMLButtonElement>(`button`)

    // If line or range is selected, scroll to first line and focus
    if (isSelectingLineOrRange) {
      const row = node.querySelector<HTMLElement>(`[data-line-anchor=diff-${selectedFullDiffHash}]`)
      if (row) {
        elementToScrollTo = row
        elementToFocus = elementToScrollTo
      }
    } else if (!initialRenderRef.current) {
      // if selecting the full entry, only scroll to it on initial render
      // after that, scroll to elements works as expected with targetable entries with html + css
      elementToScrollTo = null
    }

    if (elementToScrollTo) {
      const scrollY = ssrSafeWindow?.scrollY ?? 0
      const yOffset = elementToScrollTo.getBoundingClientRect().top + scrollY - baseDiffHeight - stickyHeaderHeight
      ssrSafeWindow?.scrollTo({top: yOffset, left: 0})
    }

    if (elementToFocus) {
      elementToFocus.focus()
    }

    initialRenderRef.current = false
  }

  /*
   * Scroll logic for non-progressive diffs. - remove after :prx_files_progressive_diff feature flag is graduated
   *
   * Scroll to and focus the selected diff entry or line(s) on page load.
   * To ensure this effect only happens on initial page load, we use a ref to track if we have run it before.
   */
  const skipScrollToAndFocus = useRef(false)
  useEffect(() => {
    if (shouldUseProgressiveDiff) return

    if (skipScrollToAndFocus.current) {
      //we are focused on the same file, no need to do the work again
      return
    }
    skipScrollToAndFocus.current = true

    const isHighlightingRightRow = selectedFullDiffHash.includes('R')
    const isHighlightingLeftRow = selectedFullDiffHash.includes('L')
    const isHighlightingRow = isHighlightingLeftRow || isHighlightingRightRow
    let pathDigestWithNoLineNumberOrHash = isHighlightingRow
      ? isHighlightingLeftRow
        ? selectedFullDiffHash.split('L')[0]
        : selectedFullDiffHash.split('R')[0]
      : selectedFullDiffHash
    pathDigestWithNoLineNumberOrHash = pathDigestWithNoLineNumberOrHash?.replace('#', '')
    //remove the leading hash if it exists, also trim the secondary line numbers off so that the first line number can
    // be focused and scrolled to
    const pathDigestWithoutHashOrDash = selectedFullDiffHash.replace('#', '').split('-')[0]
    const indexOfDiff = getDiffWithPathDigest(pathDigestWithNoLineNumberOrHash ?? selectedFullDiffHash, diffs)
    if (indexOfDiff === -1) {
      return
    }
    if (isHighlightingRow && focusRowElement(diffsParentRef, pathDigestWithoutHashOrDash ?? '')) {
      //if we are in here, then nothing else to do, as the user was selecting a row already
    } else {
      // otherwise we couldn't find the row to highlight (was a context row)  so default to just highlighting the file itself
      const allDiffLinesElements = diffsParentRef.current?.querySelectorAll(
        `[data-diff-anchor=diff-${pathDigestWithNoLineNumberOrHash}]`,
      )
      if (allDiffLinesElements && allDiffLinesElements.length === 1) {
        const elementToScrollTo = allDiffLinesElements[0] as HTMLElement
        const yOffset =
          elementToScrollTo.getBoundingClientRect().top +
          (ssrSafeWindow?.scrollY ?? 0) -
          baseDiffHeight -
          stickyHeaderHeight
        ssrSafeWindow?.scrollTo({top: yOffset, left: 0})
        const collapseButton = diffsParentRef.current?.querySelectorAll(
          `[aria-label="collapse file: ${diffs[indexOfDiff]?.path}"]`,
        )
        if (collapseButton && collapseButton.length === 1) {
          const collapseButtonToFocus = collapseButton[0] as HTMLElement
          collapseButtonToFocus.focus()
        }
      } else {
        //in case the diff was collapsed, there wouldn't be any diff lines to find, so we
        //instead scroll to the expand button
        const expandButton = diffsParentRef.current?.querySelectorAll(
          `[aria-label="expand file: ${diffs[indexOfDiff]?.path}"]`,
        )
        if (expandButton && expandButton.length === 1) {
          const expandButtonToFocus = expandButton[0] as HTMLElement
          const yOffset =
            expandButtonToFocus.getBoundingClientRect().top +
            (ssrSafeWindow?.scrollY ?? 0) -
            baseDiffHeight -
            stickyHeaderHeight
          ssrSafeWindow?.scrollTo({top: yOffset, left: 0})
          expandButtonToFocus.focus()
        }
      }
    }
  }, [selectedFullDiffHash, diffs, selectedPathDigest, shouldUseProgressiveDiff])

  const dynamicSplitPreferenceEnabled = useFeatureFlag('prx_dynamic_split_pref')
  const [diffListWidth, setDiffListWidth] = useState<ScreenSize>(ScreenSize.small)

  // Track diff list width updates
  const updateDiffListWidth = useCallback(() => {
    if (diffsParentRef.current && dynamicSplitPreferenceEnabled) {
      setDiffListWidth(getCurrentSize(diffsParentRef.current.clientWidth))
    }
  }, [dynamicSplitPreferenceEnabled])

  // Use ResizeObserver to track element size changes
  useResizeObserver(updateDiffListWidth, diffsParentRef)

  // Initial width calculation
  useEffect(() => {
    updateDiffListWidth()
  }, [updateDiffListWidth])

  // if the diff list width is less than 768px we auto switch to unified (when FF is enabled)
  const userWithModifiedSplitPreference = useMemo(
    () => ({
      ...currentUser,
      splitPreference:
        dynamicSplitPreferenceEnabled && diffListWidth < ScreenSize.large ? 'unified' : currentUser.splitPreference,
    }),
    [currentUser, dynamicSplitPreferenceEnabled, diffListWidth],
  )

  const {data: diffSummaries} = useDiffSummaries(basePath)

  const entriesToRender = useMemo(() => {
    if (!shouldUseProgressiveDiff) return []

    return diffEntries.filter(entry => {
      return filteredDiffSummaries.some(diffSummary => diffSummary.path === entry.path)
    })
  }, [shouldUseProgressiveDiff, diffEntries, filteredDiffSummaries])

  const totalVisibleEntries = shouldUseProgressiveDiff ? entriesToRender.length : diffs.length

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummaries === undefined || diffSummaries?.length === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (filteredDiffSummaries.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div ref={diffsParentRef} data-hpc {...testIdProps(testId)} className="d-flex flex-column gap-3">
      {shouldUseProgressiveDiff
        ? entriesToRender.map(progressiveDiffEntry => {
            return (
              <div
                key={progressiveDiffEntry.pathDigest}
                ref={(node: HTMLDivElement) => {
                  const map = getTargetableEntriesMap()
                  if (node) {
                    map.set(progressiveDiffEntry.pathDigest, node)
                  } else {
                    map.delete(progressiveDiffEntry.pathDigest)
                  }
                }}
              >
                <ProgressivePullRequestDiffEntry
                  basePath={basePath}
                  commentBatchPending={commentBatchPending}
                  commentBoxConfig={commentBoxConfig}
                  commentBoxSubject={commentBoxSubject}
                  commentingEnabled
                  contextLinesURL={contextLinesURL}
                  currentUser={userWithModifiedSplitPreference}
                  diffManuallyExpanded={diffManuallyExpanded}
                  headBranchName={headBranchName}
                  isSelected={progressiveDiffEntry.pathDigest === selectedPathDigest}
                  isSelectingLineOrRange={isSelectingLineOrRange}
                  markerNavigationImplementation={markerNavigationImplementation}
                  onScrollToAndFocusEntry={onScrollToAndFocusEntry}
                  progressiveDiffEntry={progressiveDiffEntry}
                  pullRequestState={pullRequestState}
                  repository={repository}
                />
              </div>
            )
          })
        : diffs.map(diffEntry => {
            return (
              <div key={diffEntry.pathDigest}>
                <ErrorBoundary fallback={<PullRequestDiffEntryErrorFallback {...diffEntry} />}>
                  <PullRequestDiff
                    commentBoxConfig={commentBoxConfig}
                    commentBatchPending={commentBatchPending}
                    commentBoxSubject={commentBoxSubject}
                    headBranchName={headBranchName}
                    basePath={basePath}
                    changeType={diffEntry.status}
                    collapsed={diffEntry.collapsed ?? diffEntry.reviewed ?? false}
                    commentingEnabled
                    contextLinesURL={contextLinesURL}
                    currentUser={userWithModifiedSplitPreference}
                    diffContext="pr"
                    diffLines={diffEntry.diffLines}
                    diffManuallyExpanded={diffManuallyExpanded}
                    diffMatches={diffEntry.diffMatches}
                    diffSize={diffEntry.diffSize}
                    helpUrl={diffEntry.helpUrl}
                    isBinary={diffEntry.isBinary}
                    isSelected={diffEntry.pathDigest === selectedPathDigest}
                    isSubmodule={diffEntry.isSubmodule}
                    isTooBig={diffEntry.isTooBig}
                    linesAdded={diffEntry.linesAdded}
                    linesChanged={diffEntry.linesChanged}
                    linesDeleted={diffEntry.linesDeleted}
                    markerNavigationImplementation={markerNavigationImplementation}
                    newCommitOid={diffEntry.newCommitOid}
                    newTreeEntry={diffEntry.newTreeEntry}
                    objectId={diffEntry.objectId}
                    oldCommitOid={diffEntry.oldCommitOid}
                    oldTreeEntry={diffEntry.oldTreeEntry}
                    path={diffEntry.path}
                    pathDigest={diffEntry.pathDigest}
                    pullRequestState={pullRequestState}
                    repository={repository}
                    reviewed={diffEntry.reviewed ?? false}
                    richDiff={diffEntry.richDiff}
                    status={diffEntry.status}
                    submodule={diffEntry.submodule}
                    truncatedReason={diffEntry.truncatedReason}
                  />
                </ErrorBoundary>
              </div>
            )
          })}
      <EndOfDiffsMessage imagePath={endOfDiffsImagePath} totalVisibleEntries={totalVisibleEntries} />

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}

function getDiffWithPathDigest(pathDigest: string, diffEntries: DiffEntry[]) {
  for (let i = 0; i < diffEntries.length; i++) {
    if (pathDigest === diffEntries[i]?.pathDigest) return i
  }
  return -1
}

// Remove after :prx_files_progressive_diff feature flag is graduated
function focusRowElement(diffsParentRef: React.RefObject<HTMLDivElement>, diffLineAnchor: string) {
  const lineWithinDiffToHighlight = diffsParentRef.current?.querySelectorAll(
    `[data-line-anchor=diff-${diffLineAnchor}]`,
  )
  if (lineWithinDiffToHighlight && lineWithinDiffToHighlight.length === 1) {
    const rowToFocus = lineWithinDiffToHighlight[0] as HTMLElement
    rowToFocus.focus()
    return true
  }
  return false
}

/*
 * Whimsy
 * Show a fun image at the end of the diff list if there's enough visible entries and the user has not enabled reduced motion.
 * TODO: Create a static image to render in place of current GIF if user prefers reduced motion.
 */
const EndOfDiffsMessage = memo(function EndOfDiffsMessage({
  imagePath,
  imageAlt = 'GIF of an octocat high fiving another octocat',
  totalVisibleEntries = 0,
}: {
  imagePath?: string
  imageAlt?: string
  totalVisibleEntries?: number
}) {
  // Reduced motion setting is based on system settings, not dotcom user settings.
  const prefersReducedMotion = usePrefersReducedMotion()

  if (!imagePath) return
  if (prefersReducedMotion) return
  if (totalVisibleEntries <= SHOW_WHIMSY_THRESHOLD) return

  return (
    <div className="d-flex flex-justify-center flex-column text-center flex-items-center py-8 px-3">
      <img src={imagePath} alt={imageAlt} style={{height: '56px'}} />
      <span className="fgColor-muted mt-2">You made it to the end!</span>
    </div>
  )
})

try{ PullRequestDiffsList.displayName ||= 'PullRequestDiffsList' } catch {}
try{ PullRequestDiffsListUnmemoized.displayName ||= 'PullRequestDiffsListUnmemoized' } catch {}
try{ EndOfDiffsMessage.displayName ||= 'EndOfDiffsMessage' } catch {}